import clsx from 'clsx'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import React, { FC, useCallback, useContext, useRef, useState } from 'react'

import SvgPlus from '@Assets/SVG/Plus'
import { TopLink } from '@Blocks/ProductList/Components/TopLink/types'
import { CategoryFacetsWithChips } from '@Components/Facet/Display/CategoryMenu/CategoryFilters'
import { SubCategorySlider } from '@Components/Facet/Display/CategoryMenu/Components/SubCategorySlider'
import { SortingAccordion } from '@Components/Facet/Display/CategoryMenu/SortingAccordion'
import { SVGIcon } from '@Components/Icons'
import { NativeModal, NativeModalBody, NativeModalFooter, NativeModalHeader, useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { PaginationArrows } from '@Components/Pagination'
import { LinkedDataV1, LinkedDataV2 } from '@Components/ProductList/LinkedData'
import { ProductListNoProducts } from '@Components/ProductList/NoProducts'
import { ProductTilesIntersectingV2 } from '@Components/ProductList/ProductTiles/Intersecting/IntersectingV2'
import { ProductTilesList } from '@Components/ProductList/ProductTiles/List'
import { RTEContent } from '@Components/RTEContent'
import { isBodyDesktop, isEventDesktop } from '@Domains/Navigation/Features/Menu/Menu'
import { Breadcrumbs } from '@Domains/Product/Features/Display/Breadcrumbs/Breadcrumbs'
import { ProductSearchContext, ProductSearchStaticContext } from '@Globals/Algolia/Context'
import { enableBodyScroll, preventBodyScroll } from '@Globals/BodyHelpers'
import { AnchorItemInjections } from '@Globals/Injections/Client'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

import { TextWithMoreAndLess } from '../../CoreComponents/TextFields/TextWithMoreAndLess'
import styles from './v2.module.scss'

export interface ProductListBlockProps {
  description: string
  enableDescription?: boolean
  enableDescriptionShowMore?: boolean
  id?: string
  topLinks: TopLink[]
}

export const ProductListBlockV2: FC<ProductListBlockProps> = ({
  description,
  enableDescription,
  enableDescriptionShowMore,
  id,
  topLinks,
}) => {
  const { t } = useTranslation()
  const [appliedFiltersOnMobile, setAppliedFiltersOnMobile] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const { page: { breadcrumbs = [] } = {} } = useContext(MetadataContext)
  const filterModalActions = useNativeModalActions()
  const { usePagination } = useContext(ProductSearchStaticContext)
  const { count, currentHits, pages, title } = useContext(ProductSearchContext)

  const scrollToTop = useCallback(() => {
    let mobileModifier = 0
    if (typeof document !== 'undefined') {
      mobileModifier = first(document.getElementsByTagName('header'))?.clientHeight || 0
    }
    if (wrapperRef.current) {
      if (document.body.style.overflow === 'hidden') {
        enableBodyScroll()
        window.scrollTo({
          behavior: 'instant',
          top: isBodyDesktop() ? wrapperRef.current.offsetTop : wrapperRef.current.offsetTop - mobileModifier,
        })
        preventBodyScroll()
      } else if (document.scrollingElement && wrapperRef.current.offsetTop < document.scrollingElement?.scrollTop) {
        window.scrollTo({
          behavior: 'instant',
          top: isBodyDesktop() ? wrapperRef.current.offsetTop : wrapperRef.current.offsetTop - mobileModifier,
        })
      }
    }
  }, [])

  const onFilterChangeMobile = useCallback(() => {
    setAppliedFiltersOnMobile(true)
    scrollToTop()
  }, [scrollToTop])

  let descriptionContent = enableDescription && description
    ? <RTEContent className={clsx(styles.description)} html={description} />
    : null
  if (descriptionContent && enableDescriptionShowMore) {
    descriptionContent = (
      <TextWithMoreAndLess
        className={styles.productListDescription}
        id="productListDescription"
        lessText={t('cms:SHOW_LESS_LABEL', '⌃')}
        moreText={t('cms:READ_MORE_LABEL', '⌄')}
      >
        {descriptionContent}
      </TextWithMoreAndLess>
    )
  }

  return (
    <div className={styles.productListBlock} id={id}>
      {!isEmpty(breadcrumbs) && (
        <Breadcrumbs breadcrumbs={breadcrumbs} categoryBreadcrumb className={styles.breadcrumbs} />
      )}
      <header className={styles.header}>
        <h1>
          {title.page && (
            <span className={styles.pageNumber}>
              {title.page}
              {' - '}
            </span>
          )}
          {title.canonical && (
            <span>
              {title.canonical}
              {' - '}
            </span>
          )}
          {title.base}
        </h1>
        {descriptionContent}
      </header>
      {!isEmpty(topLinks) && (
        <div className={styles.topLinks}>
          <SubCategorySlider topLinks={topLinks} />
        </div>
      )}
      <p className={clsx(styles.numberOfProducts, styles.numberOfProductsMobile)}>
        {t('products:CP_X_PRODUCTS', '{{arg1}} products', { arg1: currentHits })}
      </p>
      <div className={styles.actions} ref={wrapperRef}>
        <div
          className={styles.sortingAndFilters}
        >
          <div className={styles.filterWrapper}>
            <button
              id="category-filterV2"
              onClick={(e) => {
                if (!isEventDesktop(e)) {
                  filterModalActions.current?.open()
                }
              }}
              type="button"
            >
              <div className={styles.filterTitleFilters}>
                <p className={styles.filterTitleText}>
                  {t('common:FILTERS')}
                  {count > 0 && <span>{` (${count})`}</span>}
                </p>
                <SVGIcon icon={SvgPlus} size="20px" strokeColor="var(--neutral-900)" />
              </div>
            </button>

          </div>

          <p className={clsx(styles.numberOfProducts, styles.numberOfProductsTablet)}>
            {t('products:CP_X_PRODUCTS', '{{arg1}} products', { arg1: currentHits })}
          </p>
          <div className={styles.sorting}>
            <SortingAccordion />
          </div>
        </div>
        <div className={styles.productGrid}>
          {!usePagination && <ProductTilesIntersectingV2 scrollToTop={scrollToTop} />}
          {usePagination && <ProductTilesList />}
          {usePagination && <PaginationArrows onClick={scrollToTop} pages={pages} />}
          {currentHits === 0 && (
            <>
              <AnchorItemInjections anchor="no_hits.before" />
              <ProductListNoProducts />
              <AnchorItemInjections anchor="no_hits.after" />
            </>
          )}
        </div>
        <div className={styles.filtersOnDesktop}>
          <CategoryFacetsWithChips
            key="category_filter"
            className={styles.filterList}
            onFilterChange={scrollToTop}
          />
        </div>
        <div />
      </div>

      <NativeModal
        actionRef={filterModalActions}
        alignment="left"
        backdrop
        onClose={() => {
          filterModalActions.current?.close()
          if (appliedFiltersOnMobile) {
            scrollToTop()
            setAppliedFiltersOnMobile(false)
          }
        }}
      >
        <NativeModalHeader>
          {t('common:FILTERS')}
        </NativeModalHeader>
        <NativeModalBody>
          <div className={styles.filterModal}>
            <div className={styles.filterModalFilters}>
              <CategoryFacetsWithChips
                key="category_filter"
                className={styles.filterList}
                mobile
                onFilterChange={onFilterChangeMobile}
              />
            </div>
          </div>
        </NativeModalBody>
        <NativeModalFooter className={styles.footerButton}>
          <button
            className={styles.filterButtonMobil}
            onClick={(e) => {
              if (!isEventDesktop(e)) {
                scrollToTop()
                filterModalActions.current?.close()
              }
            }}
            type="button"
          >
            <p>
              {t('products:SHOW_RESULTS')}
              {` (${currentHits})`}
            </p>
          </button>
        </NativeModalFooter>

      </NativeModal>
      {!usePagination && <LinkedDataV1 description={description || ''} title={title.base} />}
      {usePagination && <LinkedDataV2 description={description || ''} title={[title.page, title.canonical, title.base].filter(Boolean).join(' - ')} />}
    </div>
  )
}
